import { PMPParent } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import EmrSectionsListDisplay from 'src/mentor/components/ScreeningReportMakingPage/printableReport/EmrSectionsListDisplay'
import {
  getSRInitialDataForSettingExistingSelectedComplaints,
  getSRInitialDataForSettingExistingSelectedDiagnosis,
  ScreeningReportBody,
  ScreeningReportBodyFirstSection,
  ScreeningReportHeader,
} from 'src/mentor/components/ScreeningReportMakingPage/ScreeningReportMakingPageComponents'
import {
  getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis,
  getInitialDataForSettingExistingSelectedClinicalDiagnosis,
} from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import ReactSpeedometer from 'react-d3-speedometer'
import { Box, Paper, Typography, alpha } from '@mui/material'

const ASSESSMENT_RESULT_COLORS = [
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#e9bbed', endColor: '#b71ac4' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
  { startColor: '#ddd6fe', endColor: '#7c3aed' },
  { startColor: '#e9bbed', endColor: '#b71ac4' },
  { startColor: '#a3f0b7', endColor: '#04d139' },
]

const SPEEDOMETER_SIZE = 200

function getRangeMinMax(rangeItem) {
  const min = rangeItem?.min ?? rangeItem?.from ?? 0
  const max = rangeItem?.max ?? rangeItem?.to ?? 0
  return { min, max }
}

function formatCompletedAtLabel(rawValue) {
  if (!rawValue) return null
  const date = new Date(rawValue)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

function getRunTitle(runItem) {
  return (
    runItem?.assessmentTitle ??
    runItem?.psychAssessmentTitle ??
    runItem?.clinicalAssessmentTitle ??
    'Assessment'
  )
}

function getRunId(runItem) {
  return runItem?.assessmentRunId ?? runItem?.id ?? null
}

function getScreeningAssessmentReportId(reportItem) {
  return reportItem?.assessmentReportId ?? reportItem?.id ?? null
}

function renderDomainDialCard(resultItem, indexResultItem) {
  const rangeList = Array.isArray(resultItem?.scoringRangeList) ? resultItem.scoringRangeList : []
  if (rangeList.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {resultItem?.domainName ?? 'Domain'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Score: {resultItem?.userScore ?? 0} / {resultItem?.totalScore ?? 0}
        </Typography>
      </Paper>
    )
  }

  const firstRange = getRangeMinMax(rangeList[0])
  const lastRange = getRangeMinMax(rangeList[rangeList.length - 1])
  const minValue = firstRange.min
  const maxValue = lastRange.max
  const userScore = Math.min(resultItem?.userScore ?? 0, maxValue)
  const matchingRange = rangeList.find((item) => {
    const { min, max } = getRangeMinMax(item)
    return min <= (resultItem?.userScore ?? 0) && (resultItem?.userScore ?? 0) <= max
  })
  const customSegmentStops = [minValue, ...rangeList.map((item) => getRangeMinMax(item).max)]
  const color = ASSESSMENT_RESULT_COLORS[indexResultItem % ASSESSMENT_RESULT_COLORS.length]

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: 2.5,
        border: 1,
        borderColor: 'divider',
        boxShadow: (theme) => `0 3px 14px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          background: `linear-gradient(135deg, ${color.startColor} 0%, ${color.endColor} 100%)`,
          color: '#1f2937',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {resultItem?.domainName ?? 'Domain'}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            width: SPEEDOMETER_SIZE,
            maxWidth: '100%',
            height: SPEEDOMETER_SIZE * 0.65,
            minHeight: 135,
            mx: 'auto',
            '& svg': { overflow: 'visible' },
          }}
        >
          <ReactSpeedometer
            customSegmentStops={customSegmentStops}
            fluidWidth
            value={userScore}
            minValue={minValue}
            maxValue={maxValue}
            needleColor="#1a1a1a"
            startColor={color.startColor}
            endColor={color.endColor}
            segments={rangeList.length}
            needleHeightRatio={0.7}
            ringWidth={26}
            needleTransitionDuration={1000}
            needleTransition="easeExpOut"
            textColor="#374151"
          />
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, textAlign: 'center' }}>
          Score: {resultItem?.userScore ?? 0} / {resultItem?.totalScore ?? 0}
        </Typography>
        {matchingRange?.title && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, fontWeight: 700, textAlign: 'center', color: color.endColor }}
          >
            {matchingRange.title}
          </Typography>
        )}
        {matchingRange?.interpretation && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            {matchingRange.interpretation}
          </Typography>
        )}
      </Box>
    </Paper>
  )
}

export default function ScreeningReportDisplay({
  reportData,
  screeningAssessmentReports = [],
  screeningSections,
  screeningReportTemplateData,
  bookedEventInfo,
  emrSectionDataList,
  emrSectionPrintSettings,
  mentorProfileData,
  removeBottomBorder = false,
  reportContainerRef = null,
  bottomBorderBorderModifier = '1px solid #E0E0E0',
  doctorSpecializations = null, // Doctor specializations for fetching screening sections
  visitType = null, // Visit type ('new' or 'repeat') for fetching screening sections
  useDebugNumbering = false, // Only use "Question 1", "Question 2" for debugging in history drawer
  screeningForSpecializations = null, // Specializations saved with the report (for history drawer)
  leaveSpace = false, // Add leaveSpace prop to support blank header space
}) {
  // Don't fetch here - let ScreeningReportBodyFirstSection handle fetching via getSectionsAndQuestionsBasedOnFilledAnswers
  // This prevents duplicate API calls and infinite loops
  const effectiveSections = screeningSections
  const conductedReports = Array.isArray(reportData?.assessmentsConducted) ? reportData.assessmentsConducted : []
  const conductedByReportId = new Map()
  const conductedByBookedEventId = new Map()
  conductedReports.forEach((item) => {
    const reportId = item?.assessmentReportId ?? null
    const eventId = item?.bookedEventId ?? null
    if (reportId) conductedByReportId.set(reportId, item)
    if (eventId) conductedByBookedEventId.set(eventId, item)
  })
  const screeningAssessmentReportsList = Array.isArray(screeningAssessmentReports) ? screeningAssessmentReports : []
  
  return (
    <PMPParent
      ref={reportContainerRef}
      style={{ borderBottom: removeBottomBorder ? 'none' : bottomBorderBorderModifier }}
    >
      <ScreeningReportHeader
        screeningReportData={reportData}
        screenerName={reportData?.mentorName}
        templateData={screeningReportTemplateData}
        timesVisit={bookedEventInfo?.patientMeetingSeqNumber}
        doctorName={mentorProfileData?.name}
        leaveSpace={leaveSpace}
      />
      <div style={{ flex: 1, padding: '20px' }}>
        <ScreeningReportBodyFirstSection
          observationAndAdvice={{
            advice: reportData?.advice || null,
            observation: reportData?.observation || null,
          }}
          selectedComplaints={getSRInitialDataForSettingExistingSelectedComplaints(
            reportData?.complaints,
          )}
          selectedDiagnosis={getSRInitialDataForSettingExistingSelectedDiagnosis(
            reportData?.diagnosis,
          )}
          primaryClinicalDiagnosis={getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis(
            reportData?.primaryClinicalDiagnosis,
          )}
          differentialClinicalDiagnoses={getInitialDataForSettingExistingSelectedClinicalDiagnosis(
            reportData?.differentialClinicalDiagnoses,
          )}
          screeningSections={effectiveSections}
          screeningQuestionAnswers={reportData?.screeningAnswers}
          reportData={reportData}
          doctorSpecializations={
            // Prioritize screeningForSpecializations from saved report, then fallback
            screeningForSpecializations ||
            doctorSpecializations ||
            reportData?.doctorSpecializations || 
            reportData?.mentorSpecializations ||
            reportData?.specializations ||
            mentorProfileData?.specializedCategories ||
            mentorProfileData?.specializations ||
            []
          }
          visitType={
            visitType ||
            reportData?.visitType || 
            bookedEventInfo?.visitType ||
            (bookedEventInfo?.patientMeetingSeqNumber > 1 ? 'repeat' : 'new')
          }
          useDebugNumbering={useDebugNumbering}
          screeningForSpecializations={screeningForSpecializations}
        />
        <ScreeningReportBody
          observationAndAdvice={{
            advice: reportData?.advice || null,
            observation: reportData?.observation || null,
          }}
          content={reportData?.content}
          suggestedBookingTherapy={reportData?.suggestedBookingTherapy}
        />
        {screeningAssessmentReportsList.length > 0 && (
          <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Screening-Linked Assessment Reports
            </Typography>
            {screeningAssessmentReportsList.map((assessmentReport, reportIndex) => {
              const reportId = getScreeningAssessmentReportId(assessmentReport)
              const reportEventId = assessmentReport?.bookedEventId ?? null
              const conductedItem =
                (reportId ? conductedByReportId.get(reportId) : null) ??
                (reportEventId ? conductedByBookedEventId.get(reportEventId) : null) ??
                null
              const reportTitle =
                conductedItem?.title ??
                assessmentReport?.title ??
                assessmentReport?.psychAssessmentTitle ??
                `Assessment Report ${reportIndex + 1}`
              const runIds = Array.isArray(assessmentReport?.assessmentRunIds) ? assessmentReport.assessmentRunIds : []
              const runs = Array.isArray(assessmentReport?.assessmentRuns) ? assessmentReport.assessmentRuns : []

              return (
                <Paper
                  key={reportId ?? reportEventId ?? `screening-assessment-report-${reportIndex}`}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    overflow: 'hidden',
                    boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      {reportTitle}
                    </Typography>
                    {runIds.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Linked run IDs: {runIds.join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {runs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No completed run details available for this linked assessment report.
                      </Typography>
                    ) : (
                      runs.map((runItem, runIndex) => {
                        const completedAtLabel = formatCompletedAtLabel(runItem?.completedAt)
                        const domainResults = Array.isArray(runItem?.domainResults) ? runItem.domainResults : []
                        const runTitle = getRunTitle(runItem)
                        const runId = getRunId(runItem)
                        return (
                          <Box
                            key={runId ?? `assessment-run-${runIndex}`}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 2.5,
                              p: 1.5,
                              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              {runTitle}
                            </Typography>
                            {completedAtLabel && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                                Completed: {completedAtLabel}
                              </Typography>
                            )}
                            {domainResults.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                Domain results are not available.
                              </Typography>
                            ) : (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                  gap: 1.5,
                                }}
                              >
                                {domainResults.map((resultItem, resultIndex) => (
                                  <Box key={`${resultItem?.domainId ?? 'domain'}-${resultIndex}`}>
                                    {renderDomainDialCard(resultItem, resultIndex)}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )
                      })
                    )}
                  </Box>
                </Paper>
              )
            })}
          </Box>
        )}
        <EmrSectionsListDisplay 
          emrSectionDataList={emrSectionDataList} 
          emrSectionPrintSettings={emrSectionPrintSettings}
        />
      </div>
    </PMPParent>
  )
}
